import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import PaystubViewer from './PaystubViewer';
import PaystubPeriodList from './PaystubPeriodList';
import AttendanceCard from './AttendanceCard';
import PayrollImportModalEnhanced from './PayrollImportModalEnhanced';
import SMSNotificationModal from './SMSNotificationModal';
import TimesheetReviewSMSModal from './TimesheetReviewSMSModal';
import EmployeeImportTool from './EmployeeImportTool';
import PayrollPeriodManagement from './PayrollPeriodManagement';
import PayrollQuestionManager from './PayrollQuestionManager';
import AttendanceEditModal from './AttendanceEditModal';
import LeaveRequestManager from './LeaveRequestManager';
import EmployeeDiagnostic from './EmployeeDiagnostic';
import RateAdjustmentManager from './RateAdjustmentManager';
import EmployeeManagement from './EmployeeManagement';
import TimesheetSyncView from './TimesheetSyncView';

const AdminDashboard = ({ onLogout, onImpersonate }) => {
  const [staff, setStaff] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeePaystubs, setSelectedEmployeePaystubs] = useState([]);
  const [selectedPaystub, setSelectedPaystub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('staff-list'); // 'staff-list' | 'employee-paystubs' | 'paystub-detail' | 'import-tool' | 'period-management' | 'payroll-questions' | 'leave-requests' | 'rate-requests' | 'employee-management' | 'timesheet-sync'
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [selectedEmployeeForSMS, setSelectedEmployeeForSMS] = useState(null);
  const [smsRecipients, setSmsRecipients] = useState([]);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [showTimesheetSMSModal, setShowTimesheetSMSModal] = useState(false);
  const [showAttendanceEditModal, setShowAttendanceEditModal] = useState(false);
  const [selectedEmployeeForAttendance, setSelectedEmployeeForAttendance] = useState(null);

  useEffect(() => {
    fetchAllStaff();
    fetchPayrollPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchStaffForPeriod(selectedPeriod);
    } else {
      fetchAllStaff();
    }
  }, [selectedPeriod]);

  const fetchPayrollPeriods = async () => {
    try {
      // Fetch payroll periods from database with status information
      const { data: dbPeriods, error: dbError } = await supabase
        .from('payroll_period')
        .select('*')
        .order('payroll_number', { ascending: false });

      if (dbError) {
        console.error('Error fetching payroll periods from database:', dbError);
      }

      // Create periods array from database data or fallback to hardcoded
      let periods = [];
      
      if (dbPeriods && dbPeriods.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        
        // Show all periods (no filtering by payroll number)
        const filteredPeriods = dbPeriods;
        
        // Sort by payroll_number descending (most recent first)
        filteredPeriods.sort((a, b) => b.payroll_number - a.payroll_number);
        
        periods = filteredPeriods.map(period => ({
          key: `${period.start_date}_${period.end_date}`,
          start: period.start_date,
          end: period.end_date,
          label: period.period_label,
          status: period.status,
          payroll_number: period.payroll_number,
          pay_date: period.pay_date,
          is_current: period.start_date <= today && period.end_date >= today,
          is_recent: period.end_date <= today && period.payroll_number >= 2513
        }));
      } else {
        // Fallback to hardcoded periods (for backward compatibility)
        const knownPeriods = [
          { start: '2025-06-23', end: '2025-07-06', label: '6/23/2025 - 7/6/2025' },
          { start: '2025-06-09', end: '2025-06-22', label: '6/9/2025 - 6/22/2025' },
          { start: '2025-05-26', end: '2025-06-08', label: '5/26/2025 - 6/8/2025' },
          { start: '2025-05-12', end: '2025-05-25', label: '5/12/2025 - 5/25/2025' },
          { start: '2025-04-28', end: '2025-05-11', label: '4/28/2025 - 5/11/2025' },
          { start: '2025-04-14', end: '2025-04-27', label: '4/14/2025 - 4/27/2025' },
          { start: '2025-03-31', end: '2025-04-13', label: '3/31/2025 - 4/13/2025' },
          { start: '2025-03-17', end: '2025-03-30', label: '3/17/2025 - 3/30/2025' },
          { start: '2025-03-03', end: '2025-03-16', label: '3/3/2025 - 3/16/2025' },
          { start: '2025-02-17', end: '2025-03-02', label: '2/17/2025 - 3/2/2025' }
        ];

        periods = knownPeriods.map(period => ({
          key: `${period.start}_${period.end}`,
          start: period.start,
          end: period.end,
          label: period.label,
          status: 'active' // Default status
        }));
      }
      
      setPayrollPeriods(periods);
      
      // Set default period: Processing status first, then Current, then most recent
      if (periods.length > 0 && !selectedPeriod) {
        let defaultPeriod = periods.find(period => period.status === 'Processing');
        
        if (!defaultPeriod) {
          defaultPeriod = periods.find(period => period.is_current);
        }
        
        if (!defaultPeriod) {
          defaultPeriod = periods[0]; // Most recent as fallback
        }
        
        setSelectedPeriod(defaultPeriod);
      }
    } catch (error) {
      console.error('Error processing payroll periods:', error);
    }
  };
  
  // Helper function to check if a period has attendance data
  const hasAttendanceInPeriod = (attendanceDates, startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (const dateStr of attendanceDates) {
      const date = new Date(dateStr);
      if (date >= start && date <= end) {
        return true;
      }
    }
    return false;
  };

  const fetchStaffForPeriod = async (period) => {
    try {
      setLoading(true);
      
      if (!period) {
        await fetchAllStaff();
        return;
      }
      
      // Get ALL employees from payroll_summary first (to ensure we show everyone)
      // Filter by the selected payroll period if available
      let payrollQuery = supabase
        .from('payroll_summary')
        .select('employee_number, employee_name, company, total_pay, total_hours, payroll_number')
        .order('employee_name');
      
      // If we have period info, filter by payroll_number
      if (period.payroll_number) {
        payrollQuery = payrollQuery.eq('payroll_number', period.payroll_number);
      }
      
      const { data: payrollData, error: payrollError } = await payrollQuery;

      if (payrollError) {
        throw payrollError;
      }

      // Get attendance data for the selected period
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('payroll_attendance')
        .select('employee_number, employee_name, company, hours')
        .gte('date', period.start)
        .lte('date', period.end);

      if (attendanceError) {
        throw attendanceError;
      }

      // Create a map to track attendance hours by employee/company
      const attendanceMap = new Map();
      attendanceData.forEach(record => {
        const key = `${record.employee_number}_${record.company}`;
        if (!attendanceMap.has(key)) {
          attendanceMap.set(key, 0);
        }
        attendanceMap.set(key, attendanceMap.get(key) + record.hours);
      });

      // Group by employee_number first to avoid duplicates
      const employeeMap = new Map();
      
      payrollData.forEach(record => {
        const employeeKey = record.employee_number;
        const companyKey = `${record.employee_number}_${record.company}`;
        const periodHours = attendanceMap.get(companyKey) || 0; // 0 if no attendance data
        
        if (!employeeMap.has(employeeKey)) {
          employeeMap.set(employeeKey, {
            employee_number: record.employee_number,
            employee_name: record.employee_name,
            companies: []
          });
        }
        
        // Add company data to the employee
        employeeMap.get(employeeKey).companies.push({
          company: record.company,
          total_pay: record.total_pay || 0,
          period_hours: periodHours
        });
      });

      // Convert to array
      const groupedStaff = Array.from(employeeMap.values());

      // Sort by employee name
      groupedStaff.sort((a, b) => a.employee_name.localeCompare(b.employee_name));

      setStaff(groupedStaff);
      setError('');
    } catch (error) {
      console.error('Error fetching staff for period:', error);
      setError('Failed to load staff data for selected period');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStaff = async () => {
    try {
      setLoading(true);
      
      // Get the most recent payroll period first
      const { data: recentPeriod, error: periodError } = await supabase
        .from('payroll_period')
        .select('payroll_number, start_date, end_date')
        .order('payroll_number', { ascending: false })
        .limit(1)
        .single();

      if (periodError) {
        console.warn('Could not fetch recent period, showing all data:', periodError);
        // Fallback: get all employees but aggregate by company
        await fetchAllStaffAggregated();
        return;
      }

      // Get payroll data for the most recent period only
      const { data: payrollData, error: payrollError } = await supabase
        .from('payroll_summary')
        .select('employee_number, employee_name, company, total_pay, total_hours')
        .eq('payroll_number', recentPeriod.payroll_number)
        .order('employee_name');

      if (payrollError) {
        throw payrollError;
      }

      // Group by employee and aggregate company data
      const groupedStaff = payrollData.reduce((acc, current) => {
        const existingEmployee = acc.find(emp => emp.employee_number === current.employee_number);
        
        if (existingEmployee) {
          // Add company data to existing employee
          existingEmployee.companies.push({
            company: current.company,
            total_pay: current.total_pay
          });
        } else {
          // Create new employee entry
          acc.push({
            employee_number: current.employee_number,
            employee_name: current.employee_name,
            companies: [{
              company: current.company,
              total_pay: current.total_pay
            }]
          });
        }
        return acc;
      }, []);

      setStaff(groupedStaff);
      setError('');
    } catch (error) {
      console.error('Error fetching staff:', error);
      setError('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  // Fallback function to aggregate all payroll data by company
  const fetchAllStaffAggregated = async () => {
    try {
      // Get all employees from payroll_summary table and aggregate by company
      const { data: payrollData, error: payrollError } = await supabase
        .from('payroll_summary')
        .select('employee_number, employee_name, company, total_pay')
        .order('employee_name');

      if (payrollError) {
        throw payrollError;
      }

      // Group by employee and aggregate company data (sum all payrolls per company)
      const groupedStaff = payrollData.reduce((acc, current) => {
        const existingEmployee = acc.find(emp => emp.employee_number === current.employee_number);
        
        if (existingEmployee) {
          // Find existing company or add new one
          const existingCompany = existingEmployee.companies.find(comp => comp.company === current.company);
          if (existingCompany) {
            // Add to existing company total
            existingCompany.total_pay = (parseFloat(existingCompany.total_pay) + parseFloat(current.total_pay || 0)).toFixed(2);
          } else {
            // Add new company
            existingEmployee.companies.push({
              company: current.company,
              total_pay: current.total_pay
            });
          }
        } else {
          // Create new employee entry
          acc.push({
            employee_number: current.employee_number,
            employee_name: current.employee_name,
            companies: [{
              company: current.company,
              total_pay: current.total_pay
            }]
          });
        }
        return acc;
      }, []);

      setStaff(groupedStaff);
      setError('');
    } catch (error) {
      console.error('Error fetching aggregated staff:', error);
      setError('Failed to load staff data');
    }
  };

  const handleEmployeeSelect = async (employee, company = null) => {
    try {
      setLoading(true);
      // First get the employee data with employee_id_kf and employee_id_koo
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('employee_number, employee_id_kf, employee_id_koo')
        .eq('employee_number', employee.employee_number)
        .single();

      if (employeeError) {
        console.warn('Error fetching employee IDs:', employeeError);
      }

      const employeeIds = employeeData || {};

      // Create a compatible employee object for existing logic with the IDs
      const employeeForSelection = {
        employee_number: employee.employee_number,
        employee_name: employee.employee_name,
        employee_id_kf: employeeIds.employee_id_kf,
        employee_id_koo: employeeIds.employee_id_koo,
        company: company || employee.companies[0].company // Use specified company or first company
      };
      setSelectedEmployee(employeeForSelection);

      // Fetch all paystubs for the selected employee (ADMIN ACCESS)
      console.log(`Admin: Fetching paystubs for employee: ${employeeForSelection.employee_number}`);
      
      const { data: allPaystubData, error: fetchError } = await supabase
        .from('payroll_summary')
        .select('*')
        .eq('employee_number', employeeForSelection.employee_number) // SECURITY: Specific employee only
        .order('payroll_number', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      // Fetch deductions for all paystubs using correct employee IDs
      const paystubsWithDeductions = await Promise.all(
        allPaystubData.map(async (paystub, index) => {
          let deductions = {};

          // Match deductions based on company and corresponding employee_id
          const companyCode = paystub.company;
          let employeeIdToMatch = null;

          if (companyCode === 'KF' && employeeIds.employee_id_kf) {
            employeeIdToMatch = employeeIds.employee_id_kf;
          } else if (companyCode === 'KOO' && employeeIds.employee_id_koo) {
            employeeIdToMatch = employeeIds.employee_id_koo;
          }

          if (employeeIdToMatch) {
            const { data: deductionsData, error: deductionsError } = await supabase
              .from('payroll_dynamics')
              .select('*')
              .eq('employee_id', employeeIdToMatch)
              .eq('company', companyCode)
              .eq('payroll_number', paystub.payroll_number)
              .limit(1);

            if (deductionsError) {
              console.warn('Error fetching deductions for employee_id:', employeeIdToMatch, deductionsError);
              // Set deductions to 0 on error
              deductions = {
                employee_deductions: 0,
                employee_taxes: 0,
                employer_deductions: 0,
                employer_taxes: 0,
                net_payment: 0
              };
            } else if (deductionsData && deductionsData.length > 0) {
              // Extract only specific deduction fields, preserve original paystub hours
              const deductionData = deductionsData[0];
              console.log(`Admin: Found deductions for payroll ${paystub.payroll_number}:`, deductionData.employee_deductions, deductionData.employee_taxes);
              deductions = {
                employee_deductions: deductionData.employee_deductions,
                employee_taxes: deductionData.employee_taxes,
                employer_deductions: deductionData.employer_deductions,
                employer_taxes: deductionData.employer_taxes,
                // Preserve original paystub hours (timesheet) and add benefit hours
                // Total hours = original timesheet hours + benefit hours (PTO, sick leave, lunch)
                pto_hp: deductionData.pto_hp || paystub.pto_hp || 0,
                sick_leave: deductionData.sick_leave || paystub.sick_leave || 0,
                lunch: deductionData.lunch || paystub.lunch || 0,
                // Add net_payment for Direct Deposit display
                net_payment: deductionData.net_payment || 0
              };
            } else {
              // No deductions found in payroll_dynamics for this payroll number
              console.log(`Admin: No deductions found for payroll ${paystub.payroll_number} - setting to 0`);
              deductions = {
                employee_deductions: 0,
                employee_taxes: 0,
                employer_deductions: 0,
                employer_taxes: 0,
                net_payment: 0
              };
            }
          } else {
            console.warn('Admin: No matching employee_id found for company:', companyCode);
            // Set deductions to 0 when no employee_id match
            deductions = {
              employee_deductions: 0,
              employee_taxes: 0,
              employer_deductions: 0,
              employer_taxes: 0,
              net_payment: 0
            };
          }

          // Fetch the correct pay period info based on payroll_number
          let payPeriodInfo = null;
          if (paystub.payroll_number) {
            const { data: periodData, error: periodError } = await supabase
              .from('payroll_period')
              .select('period_label, pay_date')
              .eq('payroll_number', paystub.payroll_number)
              .single();

            if (periodError) {
              console.warn('Error fetching period info for payroll_number:', paystub.payroll_number, periodError);
            } else if (periodData) {
              payPeriodInfo = periodData;
            }
          }

          return {
            ...paystub,
            ...deductions,
            pay_date: payPeriodInfo?.pay_date || '2025-06-27',
            pay_period: payPeriodInfo?.period_label || 'N/A',
            display_index: index + 1
          };
        })
      );

      setSelectedEmployeePaystubs(paystubsWithDeductions);
      setSelectedPaystub(null);
      setView('employee-paystubs');
      setError('');
    } catch (error) {
      console.error('Error fetching employee paystubs:', error);
      setError('Failed to load paystub data');
    } finally {
      setLoading(false);
    }
  };

  const handlePaystubSelect = (paystub) => {
    setSelectedPaystub(paystub);
    setView('paystub-detail');
  };

  const handleBackToStaffList = () => {
    setSelectedEmployee(null);
    setSelectedEmployeePaystubs([]);
    setSelectedPaystub(null);
    setView('staff-list');
  };

  const handleBackToEmployeePaystubs = () => {
    setSelectedPaystub(null);
    setView('employee-paystubs');
  };

  const handleImportComplete = (result) => {
    if (result.success) {
      // Refresh staff list after successful import
      fetchAllStaff();
      setShowImportModal(false);
    }
  };

  const handleSendSMSToAll = async () => {
    try {
      // Check if staff is loaded
      if (!staff || staff.length === 0) {
        console.warn('No staff loaded for the selected period');
        alert('Please select a payroll period with staff first');
        return;
      }

      // Fetch phone numbers and emails for all staff
      const { data: contactData, error } = await supabase
        .from('employees')
        .select('employee_number, phone, email')
        .in('employee_number', staff.map(emp => emp.employee_number));

      if (error) {
        console.warn('Could not fetch contact info:', error);
      }

      // Create flattened staff array with phone numbers and emails for SMS modal
      const flattenedStaffWithContacts = staff.map(emp => {
        const contactInfo = contactData?.find(p => p.employee_number === emp.employee_number);
        return {
          employee_number: emp.employee_number,
          employee_name: emp.employee_name,
          phone: contactInfo?.phone || null,
          email: contactInfo?.email || null,
          // Keep companies info for display but flatten structure
          companies: emp.companies
        };
      });

      console.log('Staff with contacts:', flattenedStaffWithContacts);

      // Set the flattened recipients for SMS modal
      setSmsRecipients(flattenedStaffWithContacts);
      setSelectedEmployeeForSMS(null);
      setShowSMSModal(true);
    } catch (error) {
      console.error('Error fetching contact info:', error);
      // Still show modal even if phone fetch fails
      setSmsRecipients([]); // Clear recipients on error
      setSelectedEmployeeForSMS(null);
      setShowSMSModal(true);
    }
  };

  const handleSendSMSToEmployee = async (employee) => {
    try {
      // Fetch phone number and email from employees table
      const { data: employeeData, error } = await supabase
        .from('employees')
        .select('phone, email')
        .eq('employee_number', employee.employee_number)
        .single();

      if (error) {
        console.warn('Could not fetch contact info:', error);
      }

      const employeeWithContacts = {
        ...employee,
        phone: employeeData?.phone || null,
        email: employeeData?.email || null
      };

      setSelectedEmployeeForSMS(employeeWithContacts);
      setSmsRecipients([]); // Clear bulk recipients for individual SMS
      setShowSMSModal(true);
    } catch (error) {
      console.error('Error fetching employee contact info:', error);
      // Still show modal even if contact fetch fails
      setSelectedEmployeeForSMS(employee);
      setSmsRecipients([]); // Clear bulk recipients for individual SMS
      setShowSMSModal(true);
    }
  };

  const handleEditAttendance = (employee) => {
    if (!selectedPeriod) {
      alert('Please select a payroll period first');
      return;
    }
    
    if (selectedPeriod.status !== 'Processing') {
      alert('Attendance can only be edited for payroll periods in Processing status');
      return;
    }

    setSelectedEmployeeForAttendance(employee);
    setShowAttendanceEditModal(true);
  };

  const handleSendTimesheetReviewSMS = async () => {
    try {
      // Check if staff is loaded
      if (!staff || staff.length === 0) {
        console.warn('No staff loaded for the selected period');
        alert('Please select a payroll period with staff first');
        return;
      }

      // Pre-validation: Check for multiple Processing periods (critical error check)
      const { data: processingCheck, error: processingError } = await supabase
        .from('payroll_period')
        .select('payroll_number, period_label, status')
        .eq('status', 'Processing');
      
      if (processingError) {
        console.error('Error checking Processing periods:', processingError);
        alert('Error checking payroll period status. Please try again.');
        return;
      }
      
      if (processingCheck.length === 0) {
        alert('No payroll period is in "Processing" status.\n\nPlease set a period to Processing status in Period Management before sending timesheet review SMS.');
        return;
      }
      
      if (processingCheck.length > 1) {
        const periodList = processingCheck.map(p => `• ${p.period_label} (${p.payroll_number})`).join('\n');
        alert(
          `CRITICAL ERROR: ${processingCheck.length} periods are in "Processing" status!\n\n` +
          `Only ONE period should be in Processing status at a time.\n\n` +
          `Processing periods found:\n${periodList}\n\n` +
          `Please fix this immediately in Period Management before sending SMS.`
        );
        return;
      }

      // Fetch phone numbers and emails for all staff
      const { data: contactData, error } = await supabase
        .from('employees')
        .select('employee_number, phone, email')
        .in('employee_number', staff.map(emp => emp.employee_number));

      if (error) {
        console.warn('Could not fetch contact info:', error);
      }

      // Create flattened staff array with phone numbers and emails for SMS modal
      const flattenedStaffWithContacts = staff.map(emp => {
        const contactInfo = contactData?.find(p => p.employee_number === emp.employee_number);
        return {
          employee_number: emp.employee_number,
          employee_name: emp.employee_name,
          phone: contactInfo?.phone || null,
          email: contactInfo?.email || null,
          companies: emp.companies
        };
      });

      // Set the flattened recipients for SMS modal
      setSmsRecipients(flattenedStaffWithContacts);
      setSelectedEmployeeForSMS(null);
      setShowTimesheetSMSModal(true);
    } catch (error) {
      console.error('Error fetching contact info:', error);
      setSmsRecipients([]);
      setSelectedEmployeeForSMS(null);
      setShowTimesheetSMSModal(true);
    }
  };

  const handleUpdatePeriodStatus = async (period, newStatus) => {
    try {
      console.log(`Updating period ${period.payroll_number} status to ${newStatus}`);
      
      const { error } = await supabase
        .from('payroll_period')
        .update({ status: newStatus })
        .eq('payroll_number', period.payroll_number);

      if (error) {
        throw error;
      }

      // Refresh payroll periods to show updated status
      await fetchPayrollPeriods();
      
      console.log(`Successfully updated period ${period.payroll_number} to ${newStatus}`);
    } catch (error) {
      console.error('Error updating period status:', error);
      setError(`Failed to update period status: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Professional Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
              <span className="ml-4 text-sm text-gray-500">
                {view === 'staff-list' && 'Manage employee paystubs'}
                {view === 'employee-paystubs' && selectedEmployee?.employee_name}
                {view === 'paystub-detail' && 'Paystub Details'}
                {view === 'period-management' && 'Payroll Period Management'}
                {view === 'payroll-questions' && 'Payroll Questions'}
                {view === 'leave-requests' && 'Leave Request Management'}
                {view === 'rate-requests' && 'Pay Rate Adjustment Requests'}
                {view === 'employee-diagnostic' && 'Employee Diagnostic Tool'}
                {view === 'employee-management' && 'Employee Management'}
                {view === 'timesheet-sync' && 'Timesheet Sync Data'}
              </span>
            </div>
            <button
              onClick={onLogout}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Bar */}
      {view === 'staff-list' && (
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="grid grid-cols-5 gap-2 py-3">
              <button
                onClick={() => setView('period-management')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-center leading-tight">Manage Periods</span>
              </button>
              <button
                onClick={() => setShowImportModal(true)}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-center leading-tight">Import CSV</span>
              </button>
              <button
                onClick={() => setView('import-tool')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                <span className="text-center leading-tight">Fix Missing Employees</span>
              </button>
              <button
                onClick={handleSendTimesheetReviewSMS}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-center leading-tight">Timesheet Review</span>
              </button>
              <button
                onClick={handleSendSMSToAll}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-center leading-tight">Send SMS to All</span>
              </button>
              <button
                onClick={() => setView('payroll-questions')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-center leading-tight">Payroll Questions</span>
              </button>
              <button
                onClick={() => setView('leave-requests')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-center leading-tight">Leave Requests</span>
              </button>
              <button
                onClick={() => setView('rate-requests')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span className="text-center leading-tight">Rate Requests</span>
              </button>
              <button
                onClick={() => setView('employee-diagnostic')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="text-center leading-tight">Employee Diagnostic</span>
              </button>
              <button
                onClick={() => setView('employee-management')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span className="text-center leading-tight">Employee Management</span>
              </button>
              <button
                onClick={() => setView('timesheet-sync')}
                className="flex flex-col items-center justify-center px-3 py-2 text-xs font-medium text-gray-700 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors min-h-[60px]"
              >
                <svg className="w-5 h-5 mb-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="text-center leading-tight">Timesheet Sync</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Back Button for non-staff-list views */}
      {view !== 'staff-list' && (
        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <button
              onClick={view === 'paystub-detail' ? handleBackToEmployeePaystubs : handleBackToStaffList}
              className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to {view === 'paystub-detail' ? 'Paystubs' : 'Staff List'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        )}
          
        {/* Payroll Period Selector */}
        {view === 'staff-list' && payrollPeriods.length > 0 && (
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label htmlFor="payroll-period" className="text-sm font-medium text-gray-700">
                  Payroll Period:
                </label>
                <select
                  id="payroll-period"
                  value={selectedPeriod?.key || ''}
                  onChange={(e) => {
                    const period = payrollPeriods.find(p => p.key === e.target.value);
                    setSelectedPeriod(period || null);
                  }}
                  className="block w-64 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                >
                  <option value="">All Periods</option>
                  {payrollPeriods.map((period) => (
                    <option key={period.key} value={period.key}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
              {selectedPeriod && (
                <div className="text-sm text-gray-500">
                  Showing staff who worked between {selectedPeriod.start} and {selectedPeriod.end}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        {view === 'period-management' && (
          <PayrollPeriodManagement onBack={() => setView('staff-list')} />
        )}

        {view === 'payroll-questions' && (
          <PayrollQuestionManager onBack={() => setView('staff-list')} />
        )}
        
        {view === 'leave-requests' && (
          <LeaveRequestManager onBack={() => setView('staff-list')} />
        )}
        
        {view === 'rate-requests' && (
          <div className="bg-white shadow rounded-lg p-6">
            <RateAdjustmentManager />
            <div className="mt-6 pt-4 border-t">
              <button
                onClick={() => setView('staff-list')}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                ← Back to Staff List
              </button>
            </div>
          </div>
        )}
        
        {view === 'employee-diagnostic' && (
          <EmployeeDiagnostic onSwitchToEmployee={onImpersonate} />
        )}
        
        {view === 'employee-management' && (
          <EmployeeManagement />
        )}

        {view === 'timesheet-sync' && (
          <TimesheetSyncView onBack={() => setView('staff-list')} />
        )}

        {view === 'staff-list' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Staff Members</h2>
              <p className="mt-1 text-sm text-gray-500">Click on an employee to view their paystub history</p>
            </div>
            <div className="divide-y divide-gray-200">
              {staff.map((employee) => (
                <div
                  key={employee.employee_number}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleEmployeeSelect(employee)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-indigo-600 font-medium text-sm">
                              {employee.employee_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {employee.employee_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            Employee #{employee.employee_number}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex items-center space-x-4">
                      {employee.companies.map((companyData) => (
                        <div key={companyData.company} className="text-right">
                          <div className="text-sm font-medium text-gray-900">
                            {companyData.company}
                          </div>
                          <div className="text-sm text-gray-500">
                            ${parseFloat(companyData.total_pay || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {selectedPeriod && companyData.period_hours !== undefined && (
                              <span className="ml-1">({companyData.period_hours}h)</span>
                            )}
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center space-x-2 ml-4">
                        {selectedPeriod && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditAttendance(employee);
                            }}
                            className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                            title="Edit attendance"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendSMSToEmployee(employee);
                          }}
                          className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
                          title="Send SMS"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {staff.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg">No staff members found.</div>
                <div className="text-gray-400 text-sm mt-2">
                  {selectedPeriod ? 'Try selecting a different payroll period or import payroll data.' : 'Import payroll data to see staff members.'}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'employee-paystubs' && (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <PaystubPeriodList
                paystubs={selectedEmployeePaystubs}
                selectedPaystub={selectedPaystub}
                onSelect={handlePaystubSelect}
                onLogout={() => {}} // Empty function since we handle logout in header
              />
            </div>
            {selectedEmployee && (
              <div className="bg-white shadow rounded-lg p-6">
                <AttendanceCard
                  employeeNumber={selectedEmployee.employee_number}
                  employeeIdKf={selectedEmployee.employee_id_kf}
                  employeeIdKoo={selectedEmployee.employee_id_koo}
                />
              </div>
            )}
          </div>
        )}

        {view === 'paystub-detail' && selectedPaystub && (
          <div className="bg-white shadow rounded-lg p-6">
            <PaystubViewer paystubData={selectedPaystub} />
          </div>
        )}

        {/* Import Modal */}
        <PayrollImportModalEnhanced
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />

        {/* SMS Notification Modal */}
        <SMSNotificationModal
          isOpen={showSMSModal}
          onClose={() => setShowSMSModal(false)}
          selectedEmployee={selectedEmployeeForSMS}
          employees={smsRecipients.length > 0 ? smsRecipients : staff}
          payrollNumber={selectedPeriod?.payroll_number}
        />

        {/* Timesheet Review SMS Modal */}
        <TimesheetReviewSMSModal
          isOpen={showTimesheetSMSModal}
          onClose={() => setShowTimesheetSMSModal(false)}
          employees={smsRecipients.length > 0 ? smsRecipients : staff}
          selectedPeriod={selectedPeriod}
        />

        {/* Attendance Edit Modal */}
        <AttendanceEditModal
          isOpen={showAttendanceEditModal}
          onClose={() => {
            setShowAttendanceEditModal(false);
            setSelectedEmployeeForAttendance(null);
            // Refresh staff data to show updated payroll summaries
            if (selectedPeriod) {
              fetchStaffForPeriod(selectedPeriod);
            } else {
              fetchAllStaff();
            }
          }}
          selectedPeriod={selectedPeriod}
          selectedEmployee={selectedEmployeeForAttendance}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;